/**
 * A2UIQuestionCard — the INTERACTIVE (blocking) A2UI path.
 *
 * An agent-side ask tool pends a question whose payload is an A2UI
 * surface, riding the SAME wire the credential prompt proved out in
 * production: `questions[0].header === 'A2UI'` and the surface JSON in
 * `options[0].description` (question-service passes both through
 * untouched — zero core changes). The reply goes back through the
 * existing question pipe as a single answer string:
 *
 *   [[ JSON.stringify({ event, context?, data }) ]]
 *
 * where `event` is the fired action name and `data` is the surface's
 * data-model snapshot. The server tool parses that contract.
 */
import { useMemo, useState } from 'react'
import { LayoutTemplate, Clock } from 'lucide-react'
import { A2UISurface } from './A2UISurface.js'
import { parseA2UISurface, type A2UIEvent, type A2UISurfaceDef } from './types.js'
import type { QuestionRequest } from '../hooks.js'

/** Detect + parse an A2UI question. Null → not ours, fall through to the
 *  generic QuestionPrompt (also the fail-soft path for a bad payload:
 *  the user then sees the raw question instead of nothing). */
export function parseA2UIQuestion(request: QuestionRequest): A2UISurfaceDef | null {
  const q = request.questions[0]
  if (!q || q.header !== 'A2UI') return null
  const desc = q.options?.[0]?.description
  if (!desc || !desc.startsWith('{')) return null
  try {
    return parseA2UISurface(JSON.parse(desc))
  } catch {
    return null
  }
}

interface Props {
  request: QuestionRequest
  surface: A2UISurfaceDef
  onReply: (answers: string[][]) => void
  onDismiss: () => void
  onOpenFile?: (path: string) => void
}

export function A2UIQuestionCard({ request, surface, onReply, onDismiss, onOpenFile }: Props) {
  // Freeze after the first fired event — the card unmounts when the reply
  // clears pendingQuestion upstream; this covers the in-flight window.
  const [answered, setAnswered] = useState(false)

  const question = request.questions[0]?.question
  const surfaceMemo = useMemo(() => surface, [surface])

  const handleEvent = (ev: A2UIEvent) => {
    if (answered) return
    setAnswered(true)
    onReply([[JSON.stringify({ event: ev.name, ...(ev.context ? { context: ev.context } : {}), data: ev.data })]])
  }

  return (
    <div className="kb-card" data-testid="a2ui-question">
      {/* Header — same badge anatomy as the credential card, indigo accent */}
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-2">
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'color-mix(in srgb, var(--btn-primary-bg) 16%, transparent)' }}
        >
          <LayoutTemplate size={15} style={{ color: 'var(--btn-primary-hover)' }} />
        </span>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-wider font-medium pb-0.5"
            style={{ color: 'var(--btn-primary-hover)' }}
          >
            Input requested
          </div>
          {question && <div className="text-sm font-medium themed-text truncate">{question}</div>}
        </div>
      </div>
      <div className="a2ui-prompt-body px-4 pt-y-3">
        <A2UISurface
          surface={surfaceMemo}
          onEvent={handleEvent}
          disabled={answered}
          onOpenFile={onOpenFile}
        />
      </div>
      <div className="flex items-center px-4 py-2 border-t themed-border">
        {/* NEVER disabled — if a reply is swallowed/fails, this is the only
            way out of a frozen card (the card unmounts on success anyway). */}
        <button
          onClick={onDismiss}
          className="flex items-center gap-1.5 px-2 py-3 rounded-md text-xs themed-text-muted hover:themed-text transition-colors"
        >
          <Clock size={12} />
          Do this later
        </button>
      </div>
    </div>
  )
}
